import React, { FC } from 'react';
import type { MyOrganisationCardResponse } from '@dreamai/types';
import { formatDate } from './organisationLabels';
import { describeCapacity, formatMoney } from './organisationCardLabels';
import { PaymentRow } from './OrganisationCardPayments';

interface OrganisationMyCardProps {
  mine: MyOrganisationCardResponse;
}

/**
 * What a member sees about organisation-funded purchases: whether they can make them,
 * their own limits and headroom, and their own organisation-funded purchases.
 * Limits are set by admins — there is nothing here for the member to top up.
 */
const OrganisationMyCard: FC<OrganisationMyCardProps> = ({ mine }) => {
  const { capacity, payments } = mine;
  const blocked = capacity.blockers.find(
    (b) => b.code === 'card_disabled' || b.code === 'billing_not_ready',
  );

  return (
    <div className='organisation-card' data-testid='organisation-my-card'>
      <h3 className='organisation-card-title'>
        Purchases paid by your organisation
      </h3>
      {blocked ? (
        <p className='organisation-card-text'>
          {blocked.code === 'card_disabled'
            ? `${capacity.organisationName} has not enabled organisation-funded purchases for you. An organisation admin can change this.`
            : `${capacity.organisationName} has no payment method on file, so organisation-funded purchases are unavailable until an admin adds one.`}
        </p>
      ) : (
        <p className='organisation-card-text'>
          When your secretary asks you to approve a purchase, you can have{' '}
          {capacity.organisationName} pay for it. {describeCapacity(capacity)}{' '}
          Your limits are set by the organisation&rsquo;s admins.
        </p>
      )}

      <dl className='organisation-stats'>
        {capacity.perTransactionCap !== null && (
          <div className='organisation-stat'>
            <dt>Per purchase</dt>
            <dd>
              {formatMoney(capacity.perTransactionCap, capacity.currency)}
            </dd>
          </div>
        )}
        {capacity.cycleCap !== null && capacity.cycleRemaining !== null && (
          <div className='organisation-stat'>
            <dt>Left this month</dt>
            <dd>
              {formatMoney(capacity.cycleRemaining, capacity.currency)}
              <span className='organisation-stat-sub'>
                {' '}
                of {formatMoney(capacity.cycleCap, capacity.currency)} · resets{' '}
                {formatDate(capacity.cycleResetsAt)}
              </span>
            </dd>
          </div>
        )}
        <div className='organisation-stat'>
          <dt>Approved this month</dt>
          <dd>{formatMoney(capacity.cycleCommitted, capacity.currency)}</dd>
        </div>
      </dl>

      {payments.length > 0 && (
        <div className='organisation-table-wrap'>
          <table className='organisation-table'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Purchase</th>
                <th>Approved</th>
                <th>Spent</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <PaymentRow key={p.id} payment={p} showMember={false} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrganisationMyCard;
